import UTIL from '@/utils';
import moment from 'moment';
import {
  dedupeDirectoryFlat,
  getPersonalRootDirectoryId,
} from '@/pages/core/knowledge/knowledge-permissions';
import { getFileBaseName } from '@/utils/mineru-parse';

export { getPersonalRootDirectoryId };

export function sameKnowledgeId(a, b) {
  if (a == null || b == null || a === '' || b === '') return false;
  return String(a) === String(b);
}

export function flattenDirectoryTree(nodes) {
  return dedupeDirectoryFlat(UTIL.plat(nodes || [], { LIST_KEY: 'children' }) || []);
}

export function getDirectoryDescendantIds(dirId, allDirsFlat) {
  const ids = [dirId];
  const children = (allDirsFlat || []).filter((d) => sameKnowledgeId(d.parent_id, dirId));
  children.forEach((child) => {
    ids.push(...getDirectoryDescendantIds(child.id, allDirsFlat));
  });
  return ids;
}

export function filterItemsInDirectory(items, dirId, allDirsFlat, { includeSubdirs = false } = {}) {
  if (!dirId) return [];
  const dirIds = includeSubdirs
    ? getDirectoryDescendantIds(dirId, allDirsFlat)
    : [dirId];
  const idSet = new Set(dirIds.map((id) => String(id)));
  return (items || []).filter((item) => item && idSet.has(String(item.dir_id)));
}

export function findDirectoryInTree(nodes, dirId) {
  let found = null;
  const walk = (items) => {
    (items || []).some((node) => {
      if (sameKnowledgeId(node.id, dirId)) {
        found = node;
        return true;
      }
      return walk(node.children || node.subs);
    });
  };
  walk(nodes);
  return found;
}

function normalizeDirChildren(node) {
  const raw = (node && (node.children || node.subs)) || [];
  return raw.map((child) => ({
    ...child,
    children: normalizeDirChildren(child),
  }));
}

/** 从扁平目录列表构建某父节点下的子树 */
export function buildSubTreeFromFlat(allFlat, parentId) {
  return (allFlat || [])
    .filter((d) => sameKnowledgeId(d.parent_id, parentId))
    .map((d) => ({
      ...d,
      children: buildSubTreeFromFlat(allFlat, d.id),
    }));
}

function buildDefaultPersonalRoot(rootId) {
  return {
    id: rootId,
    name: '个人目录',
    parent_id: null,
    children: [],
    _focusRoot: true,
    _virtualRoot: true,
  };
}

/**
 * 读取个人目录树：根 ID 固定为 personal-root-${session.id}。
 * 通过 /knowledge/directories 获取下级目录；若接口未返回根节点，仍展示默认个人目录根。
 */
export async function fetchPersonalDirectoryTree(api, session) {
  const rootId = getPersonalRootDirectoryId(session);
  if (!rootId) return { rootId: '', tree: [], allFlat: [] };

  const res = await api.get('/knowledge/directories');
  const dirs = (res && res.data && res.data.data) || [];
  const allFlatGlobal = dedupeDirectoryFlat(
    UTIL.plat(dirs, { LIST_KEY: 'children' }) || [],
  );

  let remoteRoot = findDirectoryInTree(dirs, rootId);

  if (!remoteRoot) {
    try {
      const oneRes = await api.get(`/knowledge/directories/${rootId}`);
      const data = (oneRes && oneRes.data && oneRes.data.data) != null
        ? oneRes.data.data
        : ((oneRes && oneRes.data) || null);
      if (data) {
        remoteRoot = data;
      }
    } catch (err) {
      // 根节点可能尚未在服务端创建，使用虚拟根
    }
  }

  const remoteChildren = remoteRoot
    ? normalizeDirChildren(remoteRoot)
    : [];
  const flatChildren = buildSubTreeFromFlat(allFlatGlobal, rootId);
  const children = remoteChildren.length ? remoteChildren : flatChildren;

  const personalRoot = remoteRoot
    ? {
      ...remoteRoot,
      id: rootId,
      name: remoteRoot.name || '个人目录',
      parent_id: null,
      children,
      _focusRoot: true,
    }
    : {
      ...buildDefaultPersonalRoot(rootId),
      children,
    };

  const tree = [personalRoot];
  return {
    rootId,
    tree,
    allFlat: flattenDirectoryTree(tree),
  };
}

export async function fetchKnowledgeItems(api) {
  const res = await api.get('/knowledge/data');
  return (res && res.data && res.data.data) || [];
}

export function resolveSourceKnowledgeItem(item, allItems) {
  if (!item) return null;
  if (item.ref && item.content) {
    return (allItems || []).find((i) => sameKnowledgeId(i.id, item.content)) || item;
  }
  return item;
}

export function getKnowledgeItemDisplayName(item, allItems) {
  const source = resolveSourceKnowledgeItem(item, allItems);
  if (!source) return '未命名';
  if (source.name && String(source.name).trim()) return String(source.name).trim();
  if (source.type === 'file' && source.content) {
    const fileName = decodeURIComponent((String(source.content).split('/').pop() || '').split('?')[0]) || '';
    return fileName || '未命名文件';
  }
  const content = String(source.content || source.markdown_content || '').trim();
  if (content) {
    const firstLine = content.split('\n')[0] || '未命名文档';
    return firstLine.slice(0, 40) + (firstLine.length > 40 ? '…' : '');
  }
  return '未命名';
}

export function getMdItemText(item, allItems) {
  const source = resolveSourceKnowledgeItem(item, allItems);
  if (!source) return '';
  if (source.type === 'md') return String(source.content || source.markdown_content || '').trim();
  if (source.type === 'text') return String(source.content || '').trim();
  return '';
}

export function findSameNameMdInDir(items, dirId, baseName, excludeId) {
  const target = String(baseName || '').trim().toLowerCase();
  if (!target || dirId == null) return null;
  return (items || []).find((item) => {
    if (!item || item.type !== 'md') return false;
    if (!sameKnowledgeId(item.dir_id, dirId)) return false;
    if (excludeId != null && sameKnowledgeId(item.id, excludeId)) return false;
    const rawName = String(item.name || '').trim();
    const name = getFileBaseName(rawName || '未命名').toLowerCase();
    return name === target || rawName.toLowerCase() === target;
  }) || null;
}

export async function saveMarkdownToKnowledge(api, { dirId, name, content, overwriteId }) {
  const payload = {
    type: 'md',
    name,
    content,
    dir_id: dirId,
  };
  if (overwriteId) {
    return api.put(`/knowledge/data/${overwriteId}`, payload);
  }
  return api.post('/knowledge/data', payload);
}

export function buildDefaultAiSaveName(stem) {
  const safe = String(stem || 'AI报告').replace(/[/\\?%*:|"<>]/g, '_').trim() || 'AI报告';
  return `${safe}_${moment().format('YYYYMMDD_HHmmss')}`;
}

export async function createPersonalSubDirectory(api, { parentId, name, parentDir }) {
  const parentDataType = parentDir && parentDir.data_type;
  const dataType = parentDataType === 'parsed' ? 'parsed' : 'raw';
  return api.post('/knowledge/directories', {
    type: 'dir',
    name: String(name || '').trim(),
    parent_id: parentId,
    data_type: dataType,
    editable: true,
    enable_upload: true,
    enable_create_sub: true,
  });
}
